import React, { useState } from 'react';
import { View, Heading, Text, Button, Flex, Card, Divider, Badge } from '@aws-amplify/ui-react';
import { formatFileSize } from '../utils/formatters';
import { generateClient } from 'aws-amplify/api';
import { AlertTriangle } from 'lucide-react';
import { manageSubscription } from '../graphql/mutations'
import { SubscriptionAction } from '../API';


const client = generateClient();

interface SubscriptionManagerProps {
    subscriptionId?: string;
    subscriptionStatus?: string;
    currentTier: string;
    storageUsed: number;
    storageLimit: number;
    subscriptionPeriodEnd?: number;
    onUpgradeClick: () => void;
    onSubscriptionUpdated?: () => void;
}

const SubscriptionManager: React.FC<SubscriptionManagerProps> = ({
    subscriptionId,
    subscriptionStatus,
    currentTier,
    storageUsed,
    storageLimit,
    subscriptionPeriodEnd,
    onUpgradeClick,
    onSubscriptionUpdated
}) => {
    const [isUpdating, setIsUpdating] = useState(false);
    const [updateError, setUpdateError] = useState<string | null>(null);
    const [updateSuccess, setUpdateSuccess] = useState<string | null>(null);
    const [actionType, setActionType] = useState<SubscriptionAction | null>(null);


    // Determine if the user has an active paid subscription
    const isPaidSubscription =
        subscriptionId &&
        (subscriptionStatus === 'active' || subscriptionStatus === 'canceling') &&
        currentTier !== 'FREE';

    // Format the subscription period end date if available
    const formatSubscriptionEndDate = () => {
        if (!subscriptionPeriodEnd) return null;

        const date = new Date(subscriptionPeriodEnd * 1000);
        return date.toLocaleDateString(undefined, {
            year: 'numeric',
            month: 'long',
            day: 'numeric'
        });
    };

    // Calculate percentage of storage used
    const percentUsed = storageLimit > 0 ?
        Math.min(100, (storageUsed / storageLimit) * 100) : 0;

    // Determine color for usage bar
    const getBarColor = () => {
        if (percentUsed > 90) return '#FF4B4B'; // Red
        if (percentUsed > 75) return '#FFAA00'; // Orange
        return '#00C48C'; // Green
    };

    // Function to handle subscription actions (cancel, reactivate)
    const handleSubscriptionAction = async (action: SubscriptionAction) => {
        if (!subscriptionId) return;

        try {
            setIsUpdating(true);
            setUpdateError(null);
            setUpdateSuccess(null);
            setActionType(action); 

            const response: any = await client.graphql({
                query: manageSubscription,
                variables: {
                    subscriptionId,
                    action
                },
                authMode: "userPool"
            });

            const result = response.data.manageSubscription;
            setUpdateSuccess(result.message);

            // Callback to refresh data
            if (onSubscriptionUpdated) {
                onSubscriptionUpdated();
            }
        } catch (err: any) {
            console.error('Error managing subscription:', err);
            setUpdateError(err.message || 'Failed to update subscription. Please try again.');
        } finally {
            setIsUpdating(false);
        }
    };

    return (
        <Card backgroundColor="#1A1A1A" padding="1.5rem" borderRadius="medium">
            <Heading level={4} marginBottom="1rem">Your Subscription</Heading>

            <Flex direction="column" gap="1.5rem">
                {/* Current Plan */}
                <Flex justifyContent="space-between" alignItems="center">
                    <Text fontWeight="bold">Current Plan</Text>
                    <Badge
                        backgroundColor={
                            currentTier === 'PREMIUM' ? "#3182CE" :
                                currentTier === 'PROFESSIONAL' ? "#6B46C1" :
                                    currentTier === 'BASIC' ? "#4A5568" : "#333333"
                        }
                        color="white"
                        padding="0.5rem 1rem"
                        fontSize="0.875rem"
                    >
                        {currentTier}
                    </Badge>
                </Flex>

                {/* Subscription Status */}
                {subscriptionId && (
                    <Flex direction="column" gap="0.5rem">
                        <Flex justifyContent="space-between" alignItems="center">
                            <Text>Status</Text>
                            <Badge
                                backgroundColor={
                                    subscriptionStatus === 'active' ? "#00C48C" :
                                        subscriptionStatus === 'past_due' ? "#FFAA00" :
                                            subscriptionStatus === 'canceling' ? "#FF4B4B" :
                                                subscriptionStatus === 'canceled' ? "#FF4B4B" :
                                                    "#AAAAAA"
                                }
                                color="white"
                                padding="0.25rem 0.75rem"
                                fontSize="0.75rem"
                            >
                                {subscriptionStatus === 'canceling' ? 'CANCELING' : subscriptionStatus?.toUpperCase()}
                            </Badge>
                        </Flex>

                        {subscriptionStatus === 'past_due' && (
                            <Text color="#FFAA00" fontSize="0.875rem">
                                There was a problem with your payment method. Please update your payment information.
                            </Text>
                        )}

                        {subscriptionStatus === 'canceling' && (
                            <Text color="#FF4B4B" fontSize="0.875rem">
                                Your subscription will be canceled at the end of the current billing period ({formatSubscriptionEndDate()}).
                            </Text>
                        )}

                        {subscriptionStatus === 'canceled' && (
                            <Text color="#FF4B4B" fontSize="0.875rem">
                                Your subscription has been canceled.
                            </Text>
                        )}
                    </Flex>
                )}

                {/* Storage Usage */}
                <View marginTop="0.5rem">
                    <Flex justifyContent="space-between" marginBottom="0.5rem">
                        <Text fontWeight="bold">{formatFileSize(storageUsed)} used</Text>
                        <Text color="gray">{formatFileSize(storageLimit)} total</Text>
                    </Flex>

                    {/* Storage Progress Bar */}
                    <View
                        backgroundColor="#333"
                        height="0.75rem"
                        borderRadius="full"
                        overflow="hidden"
                    >
                        <View
                            backgroundColor={getBarColor()}
                            height="100%"
                            width={`${percentUsed}%`}
                        />
                    </View>

                    <Text fontSize="0.8rem" color="gray" marginTop="0.25rem">
                        {percentUsed.toFixed(1)}% used
                    </Text>
                </View>

                {/* Storage Warning */}
                {percentUsed > 90 && (
                    <View
                        backgroundColor="#461B18"
                        padding="1rem"
                        borderRadius="medium"
                        marginTop="0.5rem"
                    >
                        <Flex alignItems="center" gap="0.5rem">
                            <AlertTriangle size={16} color="#FF4B4B" />
                            <Text color="#FF6B6B" fontWeight="medium">
                                You're using {percentUsed.toFixed(1)}% of your storage space.
                                {currentTier !== 'PROFESSIONAL' ?
                                    " Consider upgrading your plan to avoid running out of space." :
                                    " Please delete unused content to free up space."}
                            </Text>
                        </Flex>
                    </View>
                )}

                {/* Renewal Date */}
                {isPaidSubscription && subscriptionPeriodEnd && (
                    <View>
                        <Text fontWeight="bold" marginBottom="0.5rem">Renewal</Text>
                        <Text>Your subscription will {subscriptionStatus === 'canceling' ? 'end' : 'renew'} on {formatSubscriptionEndDate()}</Text>
                    </View>
                )}

                <Divider marginTop="0.5rem" marginBottom="0.5rem" />

                {/* Action Buttons */}
                <Flex direction="row" gap="1rem" justifyContent="flex-end">
                    {isPaidSubscription ? (
                        <>
                            {subscriptionStatus === 'active' ? (
                                <Button
                                    onClick={() => handleSubscriptionAction(SubscriptionAction.CANCEL)}
                                    variation="link"
                                    color="#FF4B4B"
                                    isDisabled={isUpdating}
                                    isLoading={isUpdating && actionType === SubscriptionAction.CANCEL}
                                    loadingText="Canceling..."
                                >
                                    Cancel Subscription
                                </Button>
                            ) : subscriptionStatus === 'canceling' ? (
                                <Button
                                    onClick={() => handleSubscriptionAction(SubscriptionAction.REACTIVATE)}
                                    variation="link"
                                    color="#00C48C"
                                    isDisabled={isUpdating}
                                    isLoading={isUpdating && actionType === SubscriptionAction.REACTIVATE}
                                    loadingText="Reactivating..."
                                >
                                    Reactivate Subscription
                                </Button>
                            ) : null}

                            <Button
                                onClick={onUpgradeClick}
                                variation="link"
                                isDisabled={isUpdating}
                            >
                                Change Plan
                            </Button>
                        </>
                    ) : (
                        <Button
                            onClick={onUpgradeClick}
                            variation="primary"
                            backgroundColor="#3B82F6"
                            isDisabled={isUpdating}
                        >
                            Upgrade Plan
                        </Button>
                    )}
                </Flex>

                {/* Success/Error Messages */}
                {updateSuccess && (
                    <Text color="#00C48C" fontSize="0.875rem" marginTop="0.5rem">
                        {updateSuccess}
                    </Text>
                )}

                {updateError && (
                    <Text color="#FF4B4B" fontSize="0.875rem" marginTop="0.5rem">
                        {updateError}
                    </Text>
                )}
            </Flex>
        </Card>
    );
};

export default SubscriptionManager;